export interface HotelSubscriptionAccessInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  /** `platform:subscriptions:read` (administrate implies read). */
  canReadPlatformSubscriptions: boolean;
  canAdminHotelBrands: boolean;
  isTenantAdmin: boolean;
  canManageBilling: boolean;
}

/**
 * Who may open the hotel plan dialog. In tenant mode (`?tenantId=`) the
 * platform admin needs `platform:subscriptions:read` (viewing; assigning needs
 * administrate, see `canAssignHotelPlans`); otherwise the tenant admin rules
 * apply.
 */
export function canManageHotelSubscriptions({
  tenantId,
  canReadPlatformSubscriptions,
  canAdminHotelBrands,
  isTenantAdmin,
  canManageBilling,
}: HotelSubscriptionAccessInput): boolean {
  if (tenantId) {
    return canReadPlatformSubscriptions;
  }

  return canAdminHotelBrands && isTenantAdmin && canManageBilling;
}

export interface AssignHotelPlansInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  canAdministerPlatformSubscriptions: boolean;
  isPlatformAdmin: boolean;
}

/**
 * Who sees Assign plan / Unassign. Only SUPER_ADMIN and GUESTERIA_ADMIN
 * (owner decision, DV-13) and only in tenant mode, where `x-tenant-id` names
 * the tenant. Tenant roles (TENANT_ADMIN, HOTEL_ADMIN, FRONT_DESK, STAFF,
 * GUEST) never assign or unassign; TENANT_ADMIN only picks a public plan for
 * a hotel that has a plan (`canSelectHotelPlan`, DV-149).
 */
export function canAssignHotelPlans({
  tenantId,
  canAdministerPlatformSubscriptions,
  isPlatformAdmin,
}: AssignHotelPlansInput): boolean {
  return (
    isPlatformAdmin && Boolean(tenantId) && canAdministerPlatformSubscriptions
  );
}

export interface TenantChoosePlansInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  /** The dialog may manage the hotel subscription (`canManage` prop). */
  canManage: boolean;
  isTenantAdmin: boolean;
  /** Tenant right `billing:administrate`. */
  canManageBilling: boolean;
  /** Tenant right `hotel_brands:administrate`. */
  canAdminHotelBrands: boolean;
}

/**
 * Who may choose a plan for a hotel, before looking at the hotel and the plan
 * (owner decision 2026-11-04, DV-149): Tenant Admin holding both tenant rights
 * the backend asks for on `PUT /hotels/:id/subscription`
 * (`billing:administrate`, `hotel_brands:administrate`), outside platform
 * tenant mode, where the platform assigns (`canAssignHotelPlans`).
 */
export function canTenantChoosePlans({
  tenantId,
  canManage,
  isTenantAdmin,
  canManageBilling,
  canAdminHotelBrands,
}: TenantChoosePlansInput): boolean {
  return (
    !tenantId &&
    canManage &&
    isTenantAdmin &&
    canManageBilling &&
    canAdminHotelBrands
  );
}

export interface SelectHotelPlanInput extends TenantChoosePlansInput {
  /** The hotel already has a plan; the first one is assigned by the platform. */
  hasHotelSubscription: boolean;
  /** The plan is public (`isDefault`); custom plans are assigned by the platform. */
  isPublicPlan: boolean;
  /** The plan is free; paid plans are assigned by the platform (backend 403). */
  isFreePlan: boolean;
}

/**
 * Who sees Select on a plan card: a tenant that may choose plans
 * (`canTenantChoosePlans`), on a free public plan, for a hotel that already has
 * a plan. The backend checks the same rules on `PUT`.
 */
export function canSelectHotelPlan({
  hasHotelSubscription,
  isPublicPlan,
  isFreePlan,
  ...rights
}: SelectHotelPlanInput): boolean {
  return (
    canTenantChoosePlans(rights) &&
    hasHotelSubscription &&
    isPublicPlan &&
    isFreePlan
  );
}

/** Price 0 is free (the backend test: price 0 needs no Stripe); anything else is paid. */
export function isFreeSubscriptionPlan(price: number | string): boolean {
  if (typeof price === "string") {
    return price.trim() !== "" && Number(price) === 0;
  }

  return price === 0;
}

/**
 * The button of the current plan is active again for a tenant that may select
 * once its subscription has ended (CANCELED, INCOMPLETE_EXPIRED): choosing it
 * creates a new subscription. In every other status, and for everyone who
 * cannot select (the platform), it stays "Current plan".
 */
export function isCurrentPlanSelectable(
  status: string | undefined,
  canSelect: boolean,
): boolean {
  return (
    canSelect && (status === "CANCELED" || status === "INCOMPLETE_EXPIRED")
  );
}

export interface PlanActionVisibilityInput {
  canAssign: boolean;
  /** `canSelectHotelPlan` for the plan of this card. */
  canSelect: boolean;
  canManage: boolean;
  isCurrent: boolean;
  paymentRequired: boolean;
}

/**
 * Buttons of a plan card: the primary one is Pay now (for whoever manages the
 * tenant billing), Assign plan (assign right) or Select (tenant choosing a
 * public plan, `canSelect`); Unassign needs the assign right and is shown on
 * the current plan only (DV-168: the platform unassigns in tenant mode).
 */
export function getPlanActionVisibility({
  canAssign,
  canSelect,
  canManage,
  isCurrent,
  paymentRequired,
}: PlanActionVisibilityInput): { showPrimary: boolean; showRemove: boolean } {
  return {
    showPrimary: paymentRequired ? canManage : canAssign || canSelect,
    showRemove: canAssign && isCurrent,
  };
}

export interface PlansDialogDescriptionInput {
  canAssign: boolean;
  /** `canTenantChoosePlans` for the dialog. */
  canTenantChoose: boolean;
  hasHotelSubscription: boolean;
  /** The plan of the hotel is still loading. */
  isLoading: boolean;
  /** Loading the plan of the hotel failed. */
  isError: boolean;
}

/**
 * Caption of the plans dialog: platform (assigns), tenant with a plan on the
 * hotel (chooses), tenant without a plan (first plan comes from the
 * platform), everyone else (read only). `null` while the hotel plan loads, so
 * a tenant never sees the caption of the other case; after a failed load the
 * tenant gets the read-only caption, since "has no plan yet" would be a guess.
 */
export function getPlansDialogDescriptionKey({
  canAssign,
  canTenantChoose,
  hasHotelSubscription,
  isLoading,
  isError,
}: PlansDialogDescriptionInput): string | null {
  if (canAssign) {
    return "hotels.subscriptions.description";
  }

  if (canTenantChoose) {
    if (isLoading) {
      return null;
    }

    if (isError) {
      return "hotels.subscriptions.descriptionReadOnly";
    }

    return hasHotelSubscription
      ? "hotels.subscriptions.descriptionSelect"
      : "hotels.subscriptions.descriptionNoPlan";
  }

  return "hotels.subscriptions.descriptionReadOnly";
}

/** Plans list is loaded for exactly those who may open the dialog. */
export function canLoadHotelSubscriptionPlans(
  input: HotelSubscriptionAccessInput,
): boolean {
  if (input.tenantId) {
    return input.canReadPlatformSubscriptions;
  }

  return input.isTenantAdmin;
}

/**
 * Pay now and feature toggles belong to the tenant: they are not offered to
 * the platform in tenant mode. Assigning and unassigning are the platform's
 * (`canAssignHotelPlans`).
 */
export function canMutateSubscriptionExtras(
  tenantId: string | undefined,
): boolean {
  return !tenantId;
}

/** Request options carrying `x-tenant-id` in tenant mode, nothing otherwise. */
export function tenantHeadersOptions(
  tenantId: string | undefined,
): { headers: Record<string, string> } | undefined {
  return tenantId ? { headers: { "x-tenant-id": tenantId } } : undefined;
}

export interface HotelBrandActionsInput {
  /** `?tenantId=` — platform entry from TenantEditPage. */
  tenantId: string | undefined;
  isPlatformAdmin: boolean;
  /** `platform:tenants:administrate`. */
  canAdministerPlatformTenants: boolean;
  /** `platform:subscriptions:administrate`. */
  canAdministerPlatformSubscriptions: boolean;
  /** Tenant permission `hotel_brands:administrate`. */
  canAdminHotelBrands: boolean;
}

export interface HotelBrandActions {
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCopy: boolean;
}

/**
 * Hotel Brands actions (owner decision 2026-10-07, DV-168): hotels are
 * created and deleted by platform admins in tenant mode (`x-tenant-id`); tenant
 * roles only edit their hotels. A copy creates a hotel and its plan, so it
 * needs `platform:tenants:administrate` and
 * `platform:subscriptions:administrate`: SUPER_ADMIN, or GUESTERIA_ADMIN in its
 * area (the backend checks the area).
 */
export function getHotelBrandActions({
  tenantId,
  isPlatformAdmin,
  canAdministerPlatformTenants,
  canAdministerPlatformSubscriptions,
  canAdminHotelBrands,
}: HotelBrandActionsInput): HotelBrandActions {
  if (tenantId) {
    const canAdminister = isPlatformAdmin && canAdministerPlatformTenants;

    return {
      canAdd: canAdminister,
      canEdit: canAdminister,
      canDelete: canAdminister,
      canCopy: canAdminister && canAdministerPlatformSubscriptions,
    };
  }

  return {
    canAdd: false,
    canEdit: canAdminHotelBrands,
    canDelete: false,
    canCopy: false,
  };
}
